import React, { useEffect, useState } from "react";

interface ChargeRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rate: number) => Promise<void> | void;
  initialRate?: number;
}

const ChargeRateModal: React.FC<ChargeRateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialRate,
}) => {
  const [value, setValue] = useState<string>(initialRate?.toString() ?? "");
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setValue(initialRate?.toString() ?? "");
    setError("");
  }, [initialRate, isOpen]);

  if (!isOpen) return null;

  const validate = (val: string): string | null => {
    if (!val.trim()) return "Please enter a commission percentage.";
    const num = Number(val);
    if (!Number.isFinite(num)) return "Enter a valid number.";
    if (!Number.isInteger(num)) return "Enter a whole number (no decimals).";
    if (num < 1 || num > 100)
      return "Commission must be between 1% and 100%.";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate(value);
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(Number(value));
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to update commission rate."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full relative">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Update Commission Rate
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Enter a whole-number percentage between 1 and 100. For example,
            enter <span className="font-medium">10</span> for <span className="font-medium">10%</span>.
          </p>
          <div className="space-y-2">
            <label className="text-sm text-gray-700">Commission (%)</label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              step={1}
              placeholder="e.g., 10 for 10%"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError("");
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargeRateModal;